import { Question, QuestionAgesExclude, QuestionGroupTraveler } from '@common-types';

function isExcludedAgeQuestion(q: { code?: string }): boolean {
    const c = (q.code || '').toUpperCase();
    return QuestionAgesExclude.includes(c);
}

const toCamelCase = (str: string) => str.toLowerCase().replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());

function getConditionalDependencies(conditionalData: any, questionCode: string, selectedValue: string): string[] {
    if (!conditionalData?.conditionalRules?.conditionalRules) {
        return [];
    }
    const ruleKey = toCamelCase(questionCode);
    const rule = conditionalData?.conditionalRules?.conditionalRules[ruleKey];
    if (!rule || !rule.dependencies) {
        return [];
    }
    return rule.dependencies[selectedValue] || [];
}

function questionId(q: Question & { id?: string }): string {
    return q.code || (q as { id?: string }).id || '';
}

type BookingVisibilityContext = {
    visible: Map<string, Question>;
    excludedCodes: Set<string>;
};

function computeVisibleBookingQuestionContext(
    questions: Question[] | undefined,
    bookingAnswers: Record<string, { value?: unknown; unit?: string }> | undefined,
    conditionalData: any
): BookingVisibilityContext {
    if (!questions?.length) {
        return { visible: new Map(), excludedCodes: new Set() };
    }

    const groups = conditionalData?.conditionalRules?.conditionalRules;
    const hasTransferMode = questions.some(
        (item) => item.code === 'TRANSFER_ARRIVAL_MODE' || item.code === 'TRANSFER_DEPARTURE_MODE'
    );

    let excludedCodes = new Set<string>();
    if (hasTransferMode) {
        excludedCodes = new Set([
            ...(Object.values(groups?.transferArrivalMode?.dependencies || {}).flat() as string[]),
            ...(Object.values(groups?.transferDepartureMode?.dependencies || {}).flat() as string[]),
        ]);
    }

    const visible = new Map<string, Question>();
    questions.forEach((q) => {
        const id = questionId(q);
        if (!QuestionGroupTraveler.includes(q.group) && !excludedCodes.has(id) && !isExcludedAgeQuestion(q)) {
            visible.set(id, q);
        }
    });

    questions.forEach((q) => {
        const id = questionId(q);
        const selectedValue = bookingAnswers?.[id]?.value;
        if (selectedValue === undefined || selectedValue === null || selectedValue === '') {
            return;
        }
        const deps = getConditionalDependencies(conditionalData, id, String(selectedValue));
        deps.forEach((depCode) => {
            if (isExcludedAgeQuestion({ code: String(depCode) })) {
                return;
            }
            const def = questions.find((q2) => questionId(q2) === String(depCode));
            if (def) {
                visible.set(String(depCode), def);
            }
        });
    });

    return { visible, excludedCodes };
}

/**
 * Nesting: each question is followed in the tree by its conditional follow-ups, in rule order
 * (instead of a flat list with dependents at the end of the form).
 */
export type BookingQuestionTreeNode = {
    question: Question;
    children: BookingQuestionTreeNode[];
};

function buildChildNodes(
    parentId: string,
    questions: Question[],
    bookingAnswers: Record<string, { value?: unknown; unit?: string }> | undefined,
    conditionalData: any,
    visible: Map<string, Question>,
    placed: Set<string>
): BookingQuestionTreeNode[] {
    const selectedValue = bookingAnswers?.[parentId]?.value;
    if (selectedValue === undefined || selectedValue === null || selectedValue === '') {
        return [];
    }
    const depCodes = getConditionalDependencies(conditionalData, parentId, String(selectedValue));
    const children: BookingQuestionTreeNode[] = [];
    for (const depCode of depCodes) {
        if (isExcludedAgeQuestion({ code: String(depCode) })) {
            continue;
        }
        if (!visible.has(String(depCode))) {
            continue;
        }
        if (placed.has(String(depCode))) {
            continue;
        }
        const def = questions.find((q2) => questionId(q2) === String(depCode));
        if (!def) {
            continue;
        }
        placed.add(String(depCode));
        const subChildren = buildChildNodes(
            String(depCode),
            questions,
            bookingAnswers,
            conditionalData,
            visible,
            placed
        );
        children.push({ question: def, children: subChildren });
    }
    return children;
}

/**
 * Top-level booking questions in product `questions` order, each with nested conditional
 * follow-ups. Dependent-only codes (e.g. transfer follow-ups) appear under their parent only.
 */
export function getVisibleBookingQuestionTree(
    questions: Question[] | undefined,
    bookingAnswers: Record<string, { value?: unknown; unit?: string }> | undefined,
    conditionalData: any
): BookingQuestionTreeNode[] {
    const { visible, excludedCodes } = computeVisibleBookingQuestionContext(questions, bookingAnswers, conditionalData);
    if (!questions?.length) {
        return [];
    }

    const placed = new Set<string>();
    const tree: BookingQuestionTreeNode[] = [];

    for (const q of questions) {
        const id = questionId(q);
        if (QuestionGroupTraveler.includes(q.group) || isExcludedAgeQuestion(q)) {
            continue;
        }
        if (excludedCodes.has(id)) {
            continue;
        }
        if (!visible.has(id)) {
            continue;
        }
        if (placed.has(id)) {
            continue;
        }
        placed.add(id);
        const children = buildChildNodes(id, questions, bookingAnswers, conditionalData, visible, placed);
        tree.push({ question: q, children });
    }

    return tree;
}

/**
 * Booking questions shown at checkout: base questions for this rate/session, plus
 * conditionally revealed questions only when the dependent id exists on `detailLabel.questions`
 * (modalities that do not include a question must not show it, even if global conditionals reference it).
 */
export function getVisibleBookingQuestionsForDetail(
    questions: Question[] | undefined,
    bookingAnswers: Record<string, { value?: unknown; unit?: string }> | undefined,
    conditionalData: any
): Question[] {
    const { visible } = computeVisibleBookingQuestionContext(questions, bookingAnswers, conditionalData);
    return Array.from(visible.values());
}
